<template>
  <div
    class="min-h-screen p-4 sm:p-6 lg:p-8 font-sans flex flex-col items-center justify-center relative overflow-hidden"
  >
    <div
      class="shadow-none sm:shadow-xl bg-white/90 backdrop-blur-lg rounded-3xl p-6 sm:p-8 lg:p-10 border border-white/40 w-full max-w-md text-center relative z-10 transform transition-all duration-500 ease-out"
    >
      <h2
        class="text-[20px] sm:text-3xl font-extrabold text-gray-900 mb-6 sm:mb-8"
      >
        Attendance Scan Result
      </h2>

      <!-- Loading State -->
      <transition
        name="fade-scale"
        mode="out-in"
      >
        <div
          v-if="isLoading"
          key="loading"
          class="py-8"
        >
          <div
            class="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin-slow mx-auto mb-6 shadow-md"
          ></div>
          <p class="text-gray-600 text-xl font-medium">
            Processing attendance...
          </p>
        </div>

        <!-- Result State -->
        <div
          v-else-if="popupMessage"
          key="result"
        >
          <div class="flex justify-center mb-6">
            <transition
              name="icon-bounce"
              appear
            >
              <div
                :key="popupType"
                :class="{
                  'bg-green-100 text-green-600': popupType === 'success',
                  'bg-red-100 text-red-600': popupType === 'error',
                  'bg-blue-100 text-blue-600': popupType === 'info',
                }"
                class="w-20 h-20 rounded-full flex items-center justify-center shadow-lg transform scale-0 animate-scale-in-icon"
              >
                <CheckCircle
                  v-if="popupType === 'success'"
                  class="w-12 h-12 animate-pulse-icon"
                />
                <XCircle
                  v-else-if="popupType === 'error'"
                  class="w-12 h-12 animate-shake-icon"
                />
                <AlertCircle
                  v-else-if="popupType === 'info'"
                  class="w-12 h-12 animate-bounce-icon"
                />
              </div>
            </transition>
          </div>

          <h3
            :class="[
              'text-4xl font-extrabold mb-4 sm:mb-5',
              popupType === 'success'
                ? 'text-green-700'
                : popupType === 'error'
                ? 'text-red-700'
                : 'text-blue-700',
            ]"
          >
            {{ popupTitle }}
          </h3>
          <p class="text-gray-700 text-lg sm:text-xl mb-4 sm:mb-5">
            {{ popupMessage }}
          </p>

          <div
            v-if="popupDetails.type || popupDetails.name"
            class="bg-gray-50 border border-gray-200 rounded-xl p-4 text-left space-y-2 mb-6 shadow-inner"
          >
            <p
              v-if="popupDetails.type"
              class="text-md text-gray-800 font-medium"
            >
              <span class="text-gray-600">Type:</span> {{ popupDetails.type }}
            </p>
            <p
              v-if="popupDetails.name"
              class="text-md text-gray-800 font-medium"
            >
              <span class="text-gray-600">Name:</span> {{ popupDetails.name }}
            </p>
          </div>

          <button
            @click="goBackToScanner"
            class="text-[12px] sm:text-lg mt-6 px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-3 mx-auto font-semibold group"
          >
            <Scan
              class="w-6 h-6 transition-transform duration-300 group-hover:rotate-6"
            />
            Scan Again
          </button>
        </div>

        <!-- No Result State -->
        <div
          v-else
          key="no-result"
          class="py-8"
        >
          <div class="flex justify-center mb-6">
            <div
              class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center shadow-md border border-gray-200"
            >
              <Scan class="w-12 h-12 text-gray-500" />
            </div>
          </div>
          <p class="text-gray-600 text-lg mb-6">
            No scan result to display. Please scan a QR code.
          </p>
          <button
            @click="goBackToScanner"
            class="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-3 mx-auto font-semibold text-lg group"
          >
            <Scan
              class="w-6 h-6 transition-transform duration-300 group-hover:rotate-6"
            />
            Go to Scanner
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from "vue";
  import { useStore } from "vuex";
  import { useRouter } from "vue-router";
  import { CheckCircle, XCircle, Scan, AlertCircle } from "lucide-vue-next"; // Import AlertCircle

  const store = useStore();
  const router = useRouter();

  const isLoading = ref(true);
  const popupMessage = ref("");
  const popupType = ref(""); // 'success', 'error', or 'info'
  const popupDetails = ref({}); // To store type and name from backend response
  onMounted(async () => {
    isLoading.value = true;
    try {
      const result = await store.dispatch("scanAttendance");
      if (result && result.message) {
        if (
          result.message === "checkin successful" ||
          result.message === "checkout successful"
        ) {
          popupType.value = "success";
        } else {
          popupType.value = "info";
        }
        popupMessage.value = result.message;
        popupDetails.value = { type: result.type, name: result.name };

        // ✅ Redirect after 5 seconds
        setTimeout(() => {
          router.replace("/");
        }, 5000);
      } else {
        popupType.value = "error";
        popupMessage.value = "Unknown response from backend.";
        popupDetails.value = {};

        setTimeout(() => {
          router.replace("/");
        }, 5000);
      }
    } catch (error) {
      console.error("Error during attendance scan:", error);

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        const errorMessage = error.response.data.message;

        if (errorMessage === "Already checked in and out today.") {
          popupType.value = "info";
        } else {
          popupType.value = "error";
        }

        popupMessage.value = errorMessage;
      } else {
        popupType.value = "error";
        popupMessage.value =
          error.message || "Attendance scan failed. Please try again.";
      }

      popupDetails.value = {};

      // ✅ Redirect after 5 seconds on error
      setTimeout(() => {
        router.replace("/");
      }, 5000);
    } finally {
      isLoading.value = false;
    }
  });

  const goBackToScanner = () => {
    router.replace("/"); // Navigate back to the scanner component
  };

  const popupTitle = computed(() => {
    if (popupType.value === "success") return "Success!";
    if (popupType.value === "error") return "Failed!";
    return "Info";
  });
</script>

<style scoped>
  /* Background Blobs Animation */
  @keyframes blob-1 {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
  }
  @keyframes blob-2 {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(-40px, 60px) scale(1.2);
    }
    66% {
      transform: translate(50px, -30px) scale(0.8);
    }
  }
  @keyframes blob-3 {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(20px, 40px) scale(0.95);
    }
    66% {
      transform: translate(-60px, -20px) scale(1.1);
    }
  }

  .animate-blob-1 {
    animation: blob-1 10s infinite alternate ease-in-out;
  }
  .animate-blob-2 {
    animation: blob-2 12s infinite alternate-reverse ease-in-out;
  }
  .animate-blob-3 {
    animation: blob-3 11s infinite alternate ease-in-out;
  }

  /* Loading Spinner */
  @keyframes spin-slow {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin-slow {
    animation: spin-slow 1.5s linear infinite;
  }

  /* Icon Animations */
  @keyframes scale-in-icon {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    80% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes pulse-icon {
    0%,
    100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
  }
  @keyframes shake-icon {
    0%,
    100% {
      transform: translateX(0);
    }
    20%,
    60% {
      transform: translateX(-8px);
    }
    40%,
    80% {
      transform: translateX(8px);
    }
  }
  @keyframes bounce-icon {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-15px);
    }
    60% {
      transform: translateY(-7px);
    }
  }

  .animate-scale-in-icon {
    animation: scale-in-icon 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)
      forwards;
  }
  .animate-pulse-icon {
    animation: pulse-icon 2s infinite ease-in-out;
  }
  .animate-shake-icon {
    animation: shake-icon 0.6s ease-in-out;
  }
  .animate-bounce-icon {
    animation: bounce-icon 1s ease-in-out;
  }

  /* Vue Transitions */
  .fade-scale-enter-active,
  .fade-scale-leave-active {
    transition: all 0.4s ease-in-out;
  }
  .fade-scale-enter-from,
  .fade-scale-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }

  .icon-bounce-enter-active {
    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .icon-bounce-enter-from {
    opacity: 0;
    transform: scale(0.5);
  }
</style>
